import { useState, useEffect, useMemo, useRef } from 'react';
import { TextInput, Button, Badge } from '@burna/monster-design-system';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { APIName } from 'config';
import useUser from 'hooks/useUser';
import useAlertWithTimeout from 'hooks/useAlertWithTimeout';
import Verify from 'components/verify';
import styles from './index.module.scss';
import Captcha from 'components/captcha';

function ForgotPassword() {
  const history = useHistory();
  const user = useUser();
  const alertWithTimeout = useAlertWithTimeout();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [verificationStageKey, setVerificationStageKey] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timeoutRef = useRef();
  const [pin, setPin] = useState('');
  const [isPasswordStage, setIsPasswordStage] = useState(false);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [{ error, success, message }, setStatus] = useState({});
  const goToVerificationStage = () => setVerificationStageKey(Math.random());
  const [captchaCode, setCaptchaCode] = useState('');
  const [refreshCaptcha, setRefreshCaptcha] = useState(null);

  useEffect(async () => {
    if (user.username) {
      history.push('/fileList');
    }
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) {
      return;
    }
    timeoutRef.current = setTimeout(
      () => setSecondsLeft(secondsLeft - 1),
      1000
    );
    return () => clearTimeout(timeoutRef.current);
  }, [secondsLeft]);

  useEffect(() => {
    if (verificationStageKey) {
      setSecondsLeft(120);
    }
  }, [verificationStageKey]);

  const phoneErrors = useMemo(() => {
    if (!phone) {
      return ['Enter phone number'];
    }
    if (phone.length !== 11 || !phone.startsWith('09')) {
      return ['Invalid phone number'];
    }
    return [];
  }, [phone]);

  const passwordErrors = useMemo(() => {
    if (!password) {
      return ['Enter password'];
    }
    const errors = [];
    if (password.length < 8) {
      errors.push('Must be at least 8 characters');
    }
    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      errors.push('Must be a combination of numbers and letters');
    }
    return errors;
  }, [password]);

  const password2Errors = useMemo(() => {
    if (!password2) {
      return ['Repeat password'];
    }
    if (password !== password2) {
      return ['Passwords do not match'];
    }
    return [];
  }, [password, password2]);

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus({ error: false, message: '' });
    setSubmitted(true);
    if (isPasswordStage) {
      if (passwordErrors.length > 0 || password2Errors.length > 0) {
        return;
      }
      setLoading(true);
      try {
        await axios.post(`/api/${APIName}/setPassword`, {
          username: phone,
          password
        });
        user.setUser({ username: phone });
        history.replace('/fileList');
        alertWithTimeout('Successful', 'Your Password has been changed!', 3000);
      } catch (e) {
        setStatus({
          error: true,
          message: 'Error connecting to server. Please Try again.'
        });
      }
      setLoading(false);
      return;
    }
    if (phoneErrors.length > 0 || !captchaCode) {
      return;
    }
    setLoading(true);
    setStatus({ error: false, message: '' });
    axios
      .post(`/api/${APIName}/verify-captcha`, {
        username: phone,
        captcha: captchaCode
      })
      .then(res => {
        axios.post(`/api/${APIName}/sendVerificationCode`, {
          username: phone,
          action: 'forgot_password'
        })
          .then(res => {
            goToVerificationStage();
            setStatus({
              error:  false,
              success: true,
              message: 'If the entered information is valid, an activation code will be sent to you via SMS.'
            });
          })
          .catch(err => {
            setStatus({
              error: true,
              message: 'Error connecting to server. Please try again.'
            });
          });
      })
      .catch(err => {
        setCaptchaCode('');
        setRefreshCaptcha(Date.now());
        if (err?.response?.status === 401) {
          let errorMessage = err?.response?.data?.message || '';
          setStatus({
            error: true,
            message: `${errorMessage} Please try again.`
          });
        } else {
          setStatus({
            error: true,
            message: 'Error connecting to server'
          });
        }
      });
    setLoading(false);
  };

  const handlePinChange = async pin => {
    setPin(pin);
    if (pin.length !== 6) {
      return;
    }
    setLoading(true);
    setStatus({ error: false, message: '' });
    try {
      await axios.post(`/api/${APIName}/confirmVerificationCode`, {
        username: phone,
        verificationCode: pin
      });
      setSubmitted(false);
      setIsPasswordStage(true);
    } catch (e) {
      const status = e && e.response && e.response.status;
      setStatus({
        error: true,
        message:
          status === 401
            ? 'The verification code is incorrect.'
            : status === 400
              ? 'The verification code has expired.'
              : 'Error connecting to server. Please Try again.'
      });
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    setStatus({ error: false, message: '' });
    try {
      await axios.post(`/api/${APIName}/sendVerificationCode`, {
        username: phone,
        action: 'forgot_password'
      });
      goToVerificationStage();
      setStatus({
        error:  false,
        success: true,
        message: 'If the entered information is valid, an activation code will be sent to you via SMS.'
      });
    } catch (e) {
      setStatus({
        error: true,
        message: 'Error connecting to server. Please Try again.'
      });
    }
    setLoading(false);
  };

  const handleEditPhone = () => {
    setStatus({
      error: false,
      message: ''
    });
    setVerificationStageKey(null);
    setCaptchaCode('');
    setRefreshCaptcha(Date.now());
  };

  return (
    <>
      <div className={styles.title}>
        {isPasswordStage
          ? 'Set Your Password'
          : verificationStageKey
            ? 'Verify!'
            : 'Forgot password'}
        <div className={styles.subtitle}>
          {isPasswordStage
            ? 'Set a new password'
            : verificationStageKey
              ? 'Enter the verification code'
              : 'Enter your phone number'}
        </div>
      </div>
      {error && (
        <Badge
          className={styles.badge}
          size="sm"
          type='error'
          icon="alert-circle"
          label={message}
        />
      )}
      {success && (
        <Badge
          className={styles.badge}
          size="sm"
          type='success'
          icon="alert-circle"
          label={message}
        />
      )}
      <form className={styles.form} onSubmit={handleSubmit} disabled={loading}>
        {isPasswordStage ? (
          <>
            <TextInput
              className={styles.textInput}
              type="password"
              title="Password"
              icon="lock"
              placeholder="Enter your new password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              errorMessage={passwordErrors}
              hasError={isSubmitted && passwordErrors.length > 0}
            />
            <TextInput
              className={styles.textInput}
              type="password"
              title="Confirm Password"
              icon="lock"
              placeholder="Repeat your new password"
              value={password2}
              onChange={e => setPassword2(e.target.value)}
              disabled={loading}
              errorMessages={password2Errors}
              hasError={isSubmitted && password2Errors.length > 0}
            />
          </>
        ) : verificationStageKey ? (
          <Verify
            secondsLeft={secondsLeft}
            pin={pin}
            onChange={handlePinChange}
            disabled={loading}
            onEdit={handleEditPhone}
            onResend={handleResend}
            message = {message}
          />
        ) : (
          <>
            <TextInput
              className={styles.textInput}
              type="tel"
              icon="user"
              title="Phone number"
              placeholder="e.g. 09123456789"
              value={phone}
              onChange={e =>
                setPhone(String(e.target.value.match(/\d{0,11}/) || ''))
              }
              disabled={loading}
              errorMessages={phoneErrors}
              hasError={isSubmitted && phoneErrors.length > 0}
            />
            <Captcha
              refreshCaptcha={refreshCaptcha}
              onCaptchaChange={setCaptchaCode}
              isSubmitted={isSubmitted}
              disabled={loading}
            />
          </>
        )}
        {(!verificationStageKey || isPasswordStage) && (
          <Button
            className={styles.submit}
            type="submit"
            text={isPasswordStage ? 'Done' : 'Send code'}
            disabled={loading}
          />
        )}
        <Button
          className={styles.back}
          type="button"
          text="Back"
          hierarchy="secondary-outlined"
          icon="chevron-left"
          onClick={() => history.goBack()}
        />
      </form>
    </>
  );
}

export default ForgotPassword;
